import { Button, Input, message, Select } from "antd";
import Title from "antd/es/typography/Title";
import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserItem, useXlsExportMaterialItemsMutation } from "shared/api";
import { useItemViewMaterialCategoryData } from "shared/hooks";
import { AccessPoints } from "shared/routers";
import { Loader, Page } from "shared/ui";
import { ItemViewTable } from "./ui";

interface ItemViewProps {
  user?: UserItem;
}

const ItemView: FC<ItemViewProps> = ({ user }) => {
  const { t } = useTranslation("", { keyPrefix: "itemView" });

  const hasEditPermission = user?.access_points?.includes(
    AccessPoints.ITEMS_EDIT
  );

  const [visibleIds, setVisibleIds] = useState<number[]>([]);
  const [globalSerialNumber, setGlobalSerialNumber] = useState("");
  const [xlsExport, { isLoading: isExporting }] =
    useXlsExportMaterialItemsMutation();

  const handleGlobalSerialNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setGlobalSerialNumber(e.target.value);
  };

  const {
    isLoadingMaterialCategories,
    adaptedMaterialCategories,
    handleSelectMaterialCategory,
    selectedMaterialCategoryItem,
  } = useItemViewMaterialCategoryData();

  const handleExportAllXLS = async (allIds: number[]) => {
    if (!allIds?.length) return;

    try {
      const blob = await xlsExport({ ids: allIds }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");

      const categoryName =
        selectedMaterialCategoryItem?.name?.replace(/\s+/g, "_") || "All";

      a.href = url;
      a.download = `export_${categoryName}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error("Export error");
    }
  };

  const selectedMaterialItem = adaptedMaterialCategories.find(
    (cat) => cat.value === selectedMaterialCategoryItem?.id
  );

  return (
    <Page title={t("title")}>
      {isLoadingMaterialCategories && <Loader type="fullscreen-backdrop" />}

      <div className="flex items-end justify-between mb-4">
        <div className="flex items-center gap-5">
          <div>
            <Title level={5}>{t("selectMaterialCategory")}</Title>

            <Select
              options={adaptedMaterialCategories}
              className="w-full"
              placeholder={t("selectMaterialCategory")}
              onChange={handleSelectMaterialCategory}
              value={selectedMaterialCategoryItem?.id || null}
            />
          </div>
          <div>
            <Title level={5}>{t("selectSerialNumber")}</Title>
            <Input
              className="w-full"
              placeholder={t("selectSerialNumber") || ""}
              value={globalSerialNumber}
              onChange={handleGlobalSerialNumberChange}
            />
          </div>
        </div>
        <Button
          type="primary"
          loading={isExporting}
          onClick={() => handleExportAllXLS(visibleIds)}
          className="flex items-center gap-2"
          style={{
            backgroundColor: "#217346",
            borderColor: "#217346",
          }}
          disabled={!visibleIds.length}
        >
          <img
            src="/xls-icon.png"
            alt="xls-icon"
            style={{ width: 22, height: 22, objectFit: "contain" }}
          />
          Export{" "}
          {selectedMaterialItem?.label ? selectedMaterialItem?.label : "All"}
        </Button>
      </div>

      <ItemViewTable
        hasEditPermission={hasEditPermission}
        globalSerialNumber={globalSerialNumber}
        selectedMaterialCategoryItem={selectedMaterialCategoryItem ?? null}
        setGlobalSerialNumber={setGlobalSerialNumber}
        handleSelectMaterialCategory={handleSelectMaterialCategory}
        onExportAll={setVisibleIds}
      />
    </Page>
  );
};

export { ItemView };
